import React from "react";
import { ChevronRight, ChevronUp } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { FOOTER, AREA_GUIDES } from "../mock";
import { useAppModals } from "./ValuationDialog";

const areaSlug = (name) => {
  const match = AREA_GUIDES.find((a) => a.name.toLowerCase() === name.toLowerCase());
  return match ? `/areas/${match.slug}` : null;
};

const USEFUL_LINK_ACTIONS = {
  "Property to Rent": { type: "section", target: "tenants" },
  "Areas We Cover": { type: "route", target: "/areas" },
  "Contact Us": { type: "section", target: "contact" },
  "Property Alerts": { type: "modal", target: "register" },
  "Property Valuation": { type: "modal", target: "valuation" },
  "HMO & Licensing Guide": { type: "route", target: "/landlords/hmo-licensing-hackney" },
};

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { openValuation, openRegister } = useAppModals();

  const goSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleAction = (action) => {
    if (!action) return;
    if (action.type === "modal") {
      action.target === "valuation" ? openValuation() : openRegister();
    } else if (action.type === "route") {
      navigate(action.target);
    } else {
      goSection(action.target);
    }
  };

  return (
    <footer className="bg-white">
      <div className="mx-auto grid max-w-[1200px] gap-10 px-5 py-16 lg:grid-cols-3">
        <div>
          <Logo size="lg" />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-gray-600">
            {FOOTER.about}
          </p>
        </div>
        <div>
          <h4 className="font-display text-xl font-500 uppercase tracking-wide text-gray-500">
            Useful Links
          </h4>
          <ul className="mt-5 space-y-3">
            {FOOTER.usefulLinks.map((l) => (
              <li key={l} className="border-b border-gray-100 pb-3">
                <button
                  onClick={() => handleAction(USEFUL_LINK_ACTIONS[l])}
                  className="flex items-center gap-2 text-left text-sm text-gray-700 transition-colors hover:text-[#ec008c]"
                >
                  <ChevronRight size={16} className="text-[#eaa32a]" />
                  {l}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-display text-xl font-500 uppercase tracking-wide text-gray-500">
            Property to Rent
          </h4>
          <ul className="mt-5 space-y-3">
            {FOOTER.areas.map((a) => {
              const to = areaSlug(a);
              return (
                <li key={a} className="border-b border-gray-100 pb-3">
                  {to ? (
                    <Link to={to} className="flex items-center gap-2 text-sm text-gray-700 transition-colors hover:text-[#ec008c]">
                      <ChevronRight size={16} className="text-[#eaa32a]" />
                      {a}
                    </Link>
                  ) : (
                    <button onClick={() => goSection("tenants")} className="flex items-center gap-2 text-left text-sm text-gray-700 transition-colors hover:text-[#ec008c]">
                      <ChevronRight size={16} className="text-[#eaa32a]" />
                      {a}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="bg-[#16323f] py-6">
        <div className="mx-auto max-w-[1200px] px-5 text-xs leading-relaxed text-white/70">
          <p>{FOOTER.legal}</p>
          <p className="mt-3">
            {"\u00a9 " + new Date().getFullYear() + " New Space. All rights reserved."}
          </p>
        </div>
      </div>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className="fixed bottom-6 right-6 z-40 grid h-11 w-11 place-items-center rounded-sm bg-[#16323f] text-white shadow-lg transition-colors hover:bg-[#ec008c]"
      >
        <ChevronUp size={22} />
      </button>
    </footer>
  );
}
